import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import {
  isToday,
  isPast,
  isFuture,
  isWithinInterval,
  isSameDay,
  startOfMonth,
  endOfMonth,
  addDays,
  startOfDay,
} from 'date-fns'

const DayDiv = ({
  calendarProps,
  day,
  value,
  handleClick,
  clickState,
  selectedFirst,
  selectedSecond,
  getSelectedDisabledCount,
}) => {
  const [isInInterval, setIsInInterval] = useState(false)
  const [isSelectedFirst, setIsSelectedFirst] = useState(false)
  const [isSelectedSecond, setIsSelectedSecond] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [minDateDisabled, setMinDateDisabled] = useState(false)
  const [restrictionDisabled, setRestrictionDisabled] = useState(false)
  const [cycleDisabled, setCycleDisabled] = useState(false)
  const [certainDaysDisabled, setCertainDaysDisabled] = useState(false)
  const [datesDisabled, setDatesDisabled] = useState(false)

  useEffect(() => {
    if (clickState === 'SECOND') {
      setIsInInterval(false)
      setIsSelectedFirst(false)
      setIsSelectedSecond(false)
      if (isSameDay(day.object, selectedFirst)) {
        setIsSelectedFirst(true)
      } else {
        setIsSelectedFirst(false)
      }
    }

    if (clickState === 'FIRST') {
      if (selectedFirst && isSameDay(day.object, selectedFirst)) {
        setIsSelectedFirst(true)
      } else {
        setIsSelectedFirst(false)
      }

      if (selectedSecond && isSameDay(day.object, selectedSecond)) {
        setIsSelectedSecond(true)
      } else {
        setIsSelectedSecond(false)
      }

      if (
        selectedFirst &&
        selectedSecond &&
        isWithinInterval(day.object, {
          start: selectedFirst,
          end: selectedSecond,
        })
      ) {
        setIsInInterval(true)
      } else {
        setIsInInterval(false)
      }
    }
  }, [clickState, selectedFirst, selectedSecond, day.object])

  useEffect(() => {
    const {
      disablePast,
      disableFuture,
      disabledDates,
      disableCertainDays,
      restrictions,
      minDayFromToday,
      startCycle,
      endCycle,
    } = calendarProps

    if (disablePast && disableFuture) {
      if (!isToday(day.object)) {
        setIsDisabled(true)
      }
    } else {
      if (disablePast) {
        if (isPast(day.object)) {
          if (isToday(day.object)) {
            setIsDisabled(false)
          } else {
            setIsDisabled(true)
          }
        } else {
          setIsDisabled(false)
        }
      }
      if (disableFuture) {
        if (isFuture(day.object)) {
          if (isToday(day.object)) {
            setIsDisabled(false)
          } else {
            setIsDisabled(true)
          }
        } else {
          setIsDisabled(false)
        }
      }
    }

    if (disabledDates) {
      if (disabledDates.includes(day.fullDate)) {
        setDatesDisabled(true)
      } else {
        setDatesDisabled(false)
      }
    }

    if (minDayFromToday) {
      if (
        isWithinInterval(day.object, {
          start: startOfDay(new Date()),
          end: addDays(new Date(), minDayFromToday - 1),
        })
      ) {
        setMinDateDisabled(true)
      } else {
        setMinDateDisabled(false)
      }
    }

    if (disableCertainDays) {
      if (disableCertainDays.includes(day.hari)) {
        setCertainDaysDisabled(true)
      } else {
        setCertainDaysDisabled(false)
      }
    }

    if (restrictions) {
      if (clickState === 'SECOND') {
        if (
          isWithinInterval(day.object, {
            start: selectedFirst,
            end: addDays(selectedFirst, restrictions - 1),
          })
        ) {
          setRestrictionDisabled(false)
        } else {
          setRestrictionDisabled(true)
        }
      }
      if (clickState === 'FIRST') {
        setRestrictionDisabled(false)
      }
    }

    if (startCycle && endCycle) {
      if (
        isWithinInterval(day.object, {
          start: startOfDay(startCycle),
          end: startOfDay(endCycle),
        })
      ) {
        setCycleDisabled(false)
      } else {
        setCycleDisabled(true)
      }
    }
  }, [day.object, value, clickState, calendarProps, selectedFirst, day.fullDate, day.hari])

  useEffect(() => {
    if (
      selectedFirst &&
      selectedSecond &&
      isWithinInterval(day.object, {
        start: selectedFirst,
        end: selectedSecond,
      }) &&
      (isDisabled ||
        restrictionDisabled ||
        cycleDisabled ||
        certainDaysDisabled ||
        datesDisabled ||
        minDateDisabled)
    ) {
      getSelectedDisabledCount(true)
    }
  }, [selectedFirst, selectedSecond]) // eslint-disable-line react-hooks/exhaustive-deps

  const getStyling = (dayObject) => {
    let backgroundColor
    let borderTopLeftRadius
    let borderBottomLeftRadius
    let borderTopRightRadius
    let borderBottomRightRadius
    let color

    const isTodaySelected = isToday(dayObject) && (isSelectedFirst || isSelectedSecond)

    if (isSelectedFirst || isSelectedSecond) {
      backgroundColor = 'alternate.main'
      color = isTodaySelected ? 'dark-text.main' : 'white.main'
    } else if (isInInterval) {
      backgroundColor = 'alternate.light'
      color = 'dark-text.main'
    } else {
      backgroundColor = 'white.main'
      color = 'dark-text.main'
    }

    if (isSelectedFirst) {
      borderTopLeftRadius = 2
      borderBottomLeftRadius = 2
    }

    if (isSelectedSecond) {
      borderTopRightRadius = 2
      borderBottomRightRadius = 2
    }

    if (day.hari === 0 && (isInInterval || isSelectedFirst || isSelectedSecond)) {
      borderTopLeftRadius = 2
      borderBottomLeftRadius = 2
    }
    if (day.hari === 6 && (isInInterval || isSelectedFirst || isSelectedSecond)) {
      borderTopRightRadius = 2
      borderBottomRightRadius = 2
    }

    if (isSameDay(day.object, startOfMonth(day.object))) {
      borderTopLeftRadius = 2
      borderBottomLeftRadius = 2
    }
    if (isSameDay(day.object, endOfMonth(day.object))) {
      borderTopRightRadius = 2
      borderBottomRightRadius = 2
    }

    if (
      isDisabled ||
      restrictionDisabled ||
      cycleDisabled ||
      certainDaysDisabled ||
      datesDisabled ||
      minDateDisabled
    ) {
      backgroundColor = 'white.main'
      color = 'light-gray-text-2.main'
    }

    if (
      (isDisabled ||
        restrictionDisabled ||
        cycleDisabled ||
        certainDaysDisabled ||
        datesDisabled ||
        minDateDisabled) &&
      (isInInterval || isSelectedFirst || isSelectedSecond)
    ) {
      backgroundColor = 'white.main'
      color = 'light-gray-text-2.main'
    }

    if (isToday(dayObject)) {
      backgroundColor = '#FCF8E3'
      color = isTodaySelected ? 'dark-text.main' : color
    }

    if (isToday(dayObject) && (isSelectedFirst || isSelectedSecond)) {
      backgroundColor = 'alternate.main'
      color = 'white.main'
    }

    return {
      backgroundColor,
      borderTopRightRadius,
      borderTopLeftRadius,
      borderBottomRightRadius,
      borderBottomLeftRadius,
      color,
    }
  }

  return (
    <Box
      key={day.fullDate}
      onClick={
        isDisabled ||
        restrictionDisabled ||
        cycleDisabled ||
        certainDaysDisabled ||
        datesDisabled ||
        minDateDisabled
          ? null
          : () => handleClick(day)
      }
      sx={{
        width: 'calc(100% / 7)',
        height: '45px',
        boxSizing: 'border-box',
        py: 0.25,
        border: '1px solid',
        borderColor: getStyling(day.object).backgroundColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        cursor:
          isDisabled ||
          restrictionDisabled ||
          cycleDisabled ||
          certainDaysDisabled ||
          datesDisabled ||
          minDateDisabled
            ? 'default'
            : 'pointer',
        ...getStyling(day.object),
        '&:hover': {
          backgroundColor:
            isSelectedFirst ||
            isSelectedSecond ||
            isInInterval ||
            isDisabled ||
            restrictionDisabled ||
            cycleDisabled ||
            certainDaysDisabled ||
            datesDisabled ||
            minDateDisabled
              ? ''
              : '#F1F3F4',
          border:
            isSelectedFirst ||
            isSelectedSecond ||
            isInInterval ||
            isDisabled ||
            restrictionDisabled ||
            cycleDisabled ||
            certainDaysDisabled ||
            datesDisabled ||
            minDateDisabled
              ? ''
              : '1px solid #F1F3F4',
        },
      }}
    >
      <Typography variant="body2" sx={{ my: 1 }} color={getStyling(day.object).color}>
        {day.day}
      </Typography>
    </Box>
  )
}

export default DayDiv
